// FLAGS: none
// Shows the first unread platform notification and marks it read on close.
import type { FC } from "react";
import { Box, Button, Typography, Dialog } from "@mui/material";
import { makeStyles } from "@mui/styles";
import CancelIcon from "@mui/icons-material/Cancel";
import { ReactComponent as WomanWorking } from "@src/components/images/woman_desk_laptop.svg";
import { alpha } from "@mui/material/styles";
import {
  useGetNotificationsQuery,
  useReadNotificationMutation,
} from "@src/queries/notifications";
import { useTranslation } from "react-i18next";
import authenticationService from "@src/services/authentication.service";

const useStyles = makeStyles((theme?: any) => ({
  headerContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: theme.spacing(2.5),
    paddingBottom: theme.spacing(0),
  },
  closeIcon: {
    color: theme.colors.primaryDarkBlue,
    "&:hover": {
      color: alpha(theme.colors.primaryDarkBlue, 0.1),
      cursor: "pointer",
    },
  },
  dialogHeader: {
    padding: theme.spacing(2.5),
  },
  titleTypography: {
    fontSize: "26px",
    fontWeight: 700,
    "line-height": "40px",
  },
  buttonTypography: {
    letterSpacing: 1.25,
  },
  contentContainer: {
    padding: theme.spacing(1, 2.5, 0, 2.5),
  },
  notificationText: {
    marginBottom: theme.spacing(3),
  },
  imageContainer: {
    display: "flex",
    justifyContent: "end",
    alignItems: "end",
    width: "100%",
    height: "100%",
  },
  image: {
    width: 143,
    height: 165,
  },
}));

const NotificationDialog: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isSupport } = authenticationService;

  const { data: notifications } = useGetNotificationsQuery();
  const { mutate: readNotification } = useReadNotificationMutation();

  const currentNotification = notifications?.[0];

  const onClose = () => {
    if (!currentNotification) return;
    readNotification(currentNotification.id);
  };

  const handleDialogClose = (_event: unknown, reason: string) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      onClose();
    }
  };

  if (isSupport() || !currentNotification) return null;

  return (
    <Dialog
      open
      onClose={handleDialogClose}
      sx={{ "& .MuiPaper-root": { width: 364, overflow: "hidden" } }}
      data-testid="notification-dialog"
    >
      <Box className={classes.headerContainer}>
        <Typography variant="h3">
          {t(`Notifications.${currentNotification.key}.Title`)}
        </Typography>
        <CancelIcon className={classes.closeIcon} onClick={onClose} />
      </Box>
      <Box className={classes.contentContainer}>
        <Typography
          variant="subtitle2"
          className={classes.notificationText}
          style={{ whiteSpace: "pre-line" }}
        >
          {t(`Notifications.${currentNotification.key}.Content`)}
        </Typography>
        <Button variant="outlined" onClick={onClose} data-testid="close-icon">
          <Typography variant="subtitle2">Got it, thank you!</Typography>
        </Button>
      </Box>
      <Box className={classes.imageContainer}>
        <WomanWorking className={classes.image} />
      </Box>
    </Dialog>
  );
};

export default NotificationDialog;
